export function getRoutine(skinType: string, timeOfDay: string) {
  const db: Record<string, Record<string, object>> = {
    oily: {
      morning: {
        skin_type: 'oily', time: 'morning', total_time: '~5 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Foaming or gel cleanser', why: 'Remove overnight oil and sweat', key_ingredients: ['salicylic acid', 'glycerin'] },
          { order: 2, step: 'Toner (optional)', product_type: 'BHA or niacinamide toner', why: 'Pore refinement, oil control', key_ingredients: ['salicylic acid 0.5-1%', 'niacinamide'] },
          { order: 3, step: 'Serum', product_type: 'Niacinamide serum', why: 'Oil control, pore minimising, brightening', key_ingredients: ['niacinamide 10%', 'zinc'] },
          { order: 4, step: 'Moisturiser', product_type: 'Lightweight gel or water-gel', why: 'Hydration without heaviness', key_ingredients: ['hyaluronic acid', 'glycerin'] },
          { order: 5, step: 'Sunscreen', product_type: 'SPF 50+ lightweight formula', why: 'UV protection — the single most anti-aging step', key_ingredients: ['UVA/UVB filters'] },
        ],
        pro_tips: ['Blotting papers beat powder for midday shine', 'Avoid touching your face', 'If you skip a step, skip the toner — never skip SPF'],
      },
      evening: {
        skin_type: 'oily', time: 'evening', total_time: '~8 min',
        steps: [
          { order: 1, step: 'First Cleanse', product_type: 'Micellar water or lightweight cleansing oil', why: 'Remove SPF, makeup, and surface pollution' },
          { order: 2, step: 'Second Cleanse', product_type: 'Foaming or gel cleanser', why: 'Deeper clean after removing surface layers', key_ingredients: ['salicylic acid'] },
          { order: 3, step: 'Exfoliant (3×/week)', product_type: '2% BHA liquid', why: 'Unclogs pores, smooths texture and refines the surface', key_ingredients: ['salicylic acid 2%'] },
          { order: 4, step: 'Treatment Serum', product_type: 'Niacinamide or azelaic acid serum', why: 'Target oil, texture flare-ups, and leftover marks', key_ingredients: ['niacinamide', 'azelaic acid'] },
          { order: 5, step: 'Moisturiser', product_type: 'Light gel moisturiser', why: 'Seal in hydration, support barrier overnight', key_ingredients: ['ceramides', 'hyaluronic acid'] },
        ],
        pro_tips: ['Skip heavy occlusives and butters — they clog pores', 'Don\'t use BHA and retinol on the same night initially', 'Consistency over complexity'],
      },
    },
    dry: {
      morning: {
        skin_type: 'dry', time: 'morning', total_time: '~6 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Cream or milk cleanser', why: 'Cleanse without stripping essential lipids', key_ingredients: ['glycerin', 'ceramides'] },
          { order: 2, step: 'Essence/Toner', product_type: 'Hydrating essence', why: 'First layer of moisture', key_ingredients: ['hyaluronic acid', 'glycerin', 'centella asiatica'] },
          { order: 3, step: 'Serum', product_type: 'Hyaluronic acid serum', why: 'Deep hydration — apply to damp skin', key_ingredients: ['hyaluronic acid (multiple weights)', 'panthenol'] },
          { order: 4, step: 'Moisturiser', product_type: 'Rich ceramide cream', why: 'Strengthen barrier, lock in all previous layers', key_ingredients: ['ceramides 1/3/6-II', 'cholesterol', 'fatty acids'] },
          { order: 5, step: 'Sunscreen', product_type: 'SPF 50+ moisturising formula', why: 'Essential protection — choose a hydrating formula', key_ingredients: ['UV filters', 'glycerin'] },
        ],
        pro_tips: ['Apply serum to skin that\'s still slightly damp from toner', 'Consider a humidifier in your bedroom', 'Don\'t skip a step — dry skin needs every layer'],
      },
      evening: {
        skin_type: 'dry', time: 'evening', total_time: '~10 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Gentle milk or cream cleanser', why: 'Cleanse without disrupting the lipid barrier' },
          { order: 2, step: 'Essence/Toner', product_type: 'Hydrating essence', why: 'Prep and first layer of hydration' },
          { order: 3, step: 'Treatment (2-3×/week)', product_type: 'Retinol 0.025-0.05% or peptide serum', why: 'Anti-aging, cell turnover — build up slowly' },
          { order: 4, step: 'Serum', product_type: 'Hyaluronic acid or ceramide serum', why: 'Deep hydration' },
          { order: 5, step: 'Rich Moisturiser', product_type: 'Ceramide-rich night cream or face oil', why: 'Intensive overnight repair and barrier restoration' },
          { order: 6, step: 'Occlusive (optional)', product_type: 'Thin layer of petroleum jelly on very dry patches', why: 'Seal everything in, prevent TEWL overnight' },
        ],
        pro_tips: ['Slugging (vaseline over full routine) 2×/week transforms very dry skin', 'Use retinol sandwiched in moisturiser to reduce irritation', 'Never go to bed with bare skin'],
      },
    },
    combination: {
      morning: {
        skin_type: 'combination', time: 'morning', total_time: '~5 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Gentle gel or balancing cleanser', why: 'Clean without stripping dry areas or leaving oily zones greasy' },
          { order: 2, step: 'Toner (optional)', product_type: 'Hydrating toner', why: 'Balance and prep' },
          { order: 3, step: 'Serum', product_type: 'Niacinamide serum', why: 'Works for both oily and dry areas simultaneously' },
          { order: 4, step: 'Moisturiser', product_type: 'Lightweight lotion', why: 'Light enough for T-zone, sufficient for cheeks' },
          { order: 5, step: 'Sunscreen', product_type: 'SPF 50+ (lightweight)', why: 'Always' },
        ],
        pro_tips: ['Zone-treat: lighter products on T-zone, richer on cheeks if needed'],
      },
      evening: {
        skin_type: 'combination', time: 'evening', total_time: '~7 min',
        steps: [
          { order: 1, step: 'First Cleanse', product_type: 'Micellar water', why: 'Remove SPF and daily buildup' },
          { order: 2, step: 'Second Cleanse', product_type: 'Gel cleanser', why: 'Clean slate' },
          { order: 3, step: 'Exfoliant (2×/week)', product_type: 'Mild AHA (5-8%) or BHA on T-zone', why: 'Smooth texture, clear pores' },
          { order: 4, step: 'Serum', product_type: 'Niacinamide + hyaluronic acid', why: 'Balance and hydrate' },
          { order: 5, step: 'Moisturiser', product_type: 'Gel-cream', why: 'Universal hydration' },
        ],
        pro_tips: ['No single product solves everything for combination skin — embrace zone-treating'],
      },
    },
    sensitive: {
      morning: {
        skin_type: 'sensitive', time: 'morning', total_time: '~4 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Fragrance-free milk or micellar cleanser', why: 'Gentlest possible cleanse' },
          { order: 2, step: 'Serum (optional)', product_type: 'Centella asiatica or panthenol serum', why: 'Calm and barrier support — skip if skin is reacting' },
          { order: 3, step: 'Moisturiser', product_type: 'Ceramide-rich, fragrance-free cream', why: 'Barrier repair and protection' },
          { order: 4, step: 'Sunscreen', product_type: 'Mineral SPF 50 (zinc oxide)', why: 'Sun protection without triggering reactions' },
        ],
        pro_tips: ['Fewer products = fewer potential triggers', 'Introduce one new product at a time, 2 weeks apart'],
      },
      evening: {
        skin_type: 'sensitive', time: 'evening', total_time: '~4 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Fragrance-free cream cleanser', why: 'Remove daily buildup gently' },
          { order: 2, step: 'Treatment (1-2×/week max)', product_type: 'Azelaic acid 10% or very low % niacinamide', why: 'Only when skin is calm and stable' },
          { order: 3, step: 'Moisturiser', product_type: 'Rich ceramide cream', why: 'Overnight barrier restoration' },
        ],
        pro_tips: ['Skip actives if your skin is red, tight, or irritated — let it calm down first', 'Patch test on your inner arm before your face'],
      },
    },
    normal: {
      morning: {
        skin_type: 'normal', time: 'morning', total_time: '~5 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Gentle gel or cream cleanser' },
          { order: 2, step: 'Vitamin C Serum', product_type: '10-15% L-ascorbic acid', why: 'Brighten, protect, boost collagen' },
          { order: 3, step: 'Moisturiser', product_type: 'Lightweight cream' },
          { order: 4, step: 'Sunscreen', product_type: 'SPF 50+' },
        ],
        pro_tips: ['Normal skin has flexibility — you can experiment more than other types', 'Don\'t skip SPF because you \'don\'t burn easily\''],
      },
      evening: {
        skin_type: 'normal', time: 'evening', total_time: '~6 min',
        steps: [
          { order: 1, step: 'Cleanser', product_type: 'Gentle cleanser' },
          { order: 2, step: 'Treatment', product_type: 'Retinol 0.025-0.1% (2-3×/week) or AHA (2×/week)', why: 'Anti-aging and cell turnover' },
          { order: 3, step: 'Moisturiser', product_type: 'Medium-weight cream', why: 'Hydrate and seal' },
        ],
        pro_tips: ['Build up actives slowly even on normal skin', 'Rest nights (just cleanser + moisturiser) are important'],
      },
    },
  };

  const sKey = skinType.toLowerCase();
  const tKey = timeOfDay.toLowerCase();
  const fallback = {
    skin_type: skinType,
    time_of_day: timeOfDay,
    steps: [
      { order: 1, step: 'Cleanser', product_type: 'Gentle cleanser suitable for your skin type' },
      { order: 2, step: 'Moisturiser', product_type: 'Moisturiser matched to skin type' },
      ...(timeOfDay === 'morning'
        ? [{ order: 3, step: 'Sunscreen', product_type: 'SPF 50+ — non-negotiable' }]
        : [{ order: 3, step: 'Treatment', product_type: 'Any active serum you\'re using' }]),
    ],
    pro_tips: ['Consistency over complexity', 'Patch test new products', 'SPF every morning, no exceptions'],
  };
  if (!Object.prototype.hasOwnProperty.call(db, sKey)) {
    return fallback;
  }
  return db[sKey][tKey] ?? fallback;
}
